/**
 * Utility for matching parsed spreadsheet user names against existing company members.
 */

/**
 * Splits a raw name string into { firstName, lastName }.
 * Handles "Last, First", "First Last", and single-word names.
 *
 * @param {string} rawName
 * @returns {{ firstName: string, lastName: string }}
 */
export function parseFullName(rawName) {
  if (!rawName || typeof rawName !== 'string') {
    return { firstName: '', lastName: '' };
  }

  const trimmed = rawName.trim();
  if (!trimmed) {
    return { firstName: '', lastName: '' };
  }

  // "Last, First" format
  if (trimmed.includes(',')) {
    const parts = trimmed.split(',').map((p) => p.trim());
    const lastName = parts[0] || '';
    const firstName = parts.slice(1).join(' ').trim() || '';
    return { firstName, lastName };
  }

  // "First Last" or "First Middle Last" format
  const words = trimmed.split(/\s+/);
  if (words.length === 1) {
    return { firstName: words[0], lastName: '' };
  }

  const firstName = words[0];
  const lastName = words.slice(1).join(' ');
  return { firstName, lastName };
}

/**
 * Normalise a string for comparison: lowercase, trim, collapse whitespace.
 *
 * @param {string} str
 * @returns {string}
 */
function normalize(str) {
  if (!str) return '';
  return str.toLowerCase().trim().replace(/\s+/g, ' ');
}

/**
 * Compute the Levenshtein edit-distance between two strings.
 *
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
export function levenshtein(a, b) {
  const m = a.length;
  const n = b.length;

  if (m === 0) return n;
  if (n === 0) return m;

  // Use a single-row DP approach for memory efficiency
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  let curr = new Array(n + 1);

  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1,       // deletion
        curr[j - 1] + 1,   // insertion
        prev[j - 1] + cost  // substitution
      );
    }
    [prev, curr] = [curr, prev];
  }

  return prev[n];
}

/**
 * Simple fuzzy string matching between two name strings.
 *
 * - Normalises both strings (lowercase, trim, collapse whitespace)
 * - Checks if all words from the shorter name appear in the longer name
 *   (handles "John Smith" matching "John David Smith")
 * - Falls back to Levenshtein-based similarity for close typos
 * - Returns a score between 0 and 1
 *
 * @param {string} name1
 * @param {string} name2
 * @returns {number} Score between 0 and 1
 */
export function fuzzyNameScore(name1, name2) {
  const n1 = normalize(name1);
  const n2 = normalize(name2);

  if (!n1 || !n2) return 0;
  if (n1 === n2) return 1;

  const words1 = n1.split(' ');
  const words2 = n2.split(' ');

  // Determine shorter and longer word lists
  const shorter = words1.length <= words2.length ? words1 : words2;
  const longer = words1.length <= words2.length ? words2 : words1;

  // Check if every word from the shorter name appears in the longer name
  const allWordsFound = shorter.every((word) =>
    longer.some((w) => w === word)
  );

  if (allWordsFound && shorter.length > 0) {
    // All words present — score based on how many extra words the longer name has
    // e.g. "John Smith" vs "John David Smith" → 2/3 words matched from shorter perspective = high
    const ratio = shorter.length / longer.length;
    // Scale between 0.8 and 1.0 based on ratio
    return 0.8 + 0.2 * ratio;
  }

  // Check for fuzzy word-level matches (handles typos like "Jonh" vs "John")
  let matchedWords = 0;
  const usedIndices = new Set();

  for (const word of shorter) {
    let bestScore = 0;
    let bestIdx = -1;

    for (let i = 0; i < longer.length; i++) {
      if (usedIndices.has(i)) continue;

      const maxLen = Math.max(word.length, longer[i].length);
      if (maxLen === 0) continue;

      const dist = levenshtein(word, longer[i]);
      const similarity = 1 - dist / maxLen;

      if (similarity > bestScore) {
        bestScore = similarity;
        bestIdx = i;
      }
    }

    // Accept fuzzy word match if similarity is above threshold
    if (bestScore >= 0.7 && bestIdx !== -1) {
      matchedWords += bestScore;
      usedIndices.add(bestIdx);
    }
  }

  if (shorter.length === 0) return 0;

  // Overall score: proportion of matched words, scaled by coverage
  const wordMatchRatio = matchedWords / shorter.length;
  const coverageRatio = shorter.length / longer.length;

  return wordMatchRatio * (0.7 + 0.3 * coverageRatio);
}

/**
 * Build the full name string from a member profile for comparison.
 *
 * @param {{ first_name?: string, last_name?: string }} profile
 * @returns {string}
 */
function profileFullName(profile) {
  const parts = [profile.first_name, profile.last_name].filter(Boolean);
  return parts.join(' ');
}

/**
 * Matches parsed user names from a spreadsheet against existing company members.
 *
 * Matching priority:
 *   1. Exact email match (case-insensitive) -> confidence 1.0, action 'match'
 *   2. Exact full name match (case-insensitive, trimmed) -> confidence 0.95, action 'match'
 *   3. Fuzzy name match (score >= 0.6) -> action 'match'
 *   4. No match -> confidence 0, action 'create_new'
 *
 * @param {Array<{ rawName: string, email?: string, rowIndex: number }>} parsedUsers
 * @param {Array<{ id: string, email?: string, first_name?: string, last_name?: string, avatar_url?: string }>} companyMembers
 * @returns {Array<{ rawName: string, email?: string, rowIndex: number, matchType: string, confidence: number, matchedProfile: object|null, action: string }>}
 */
export function matchUsers(parsedUsers, companyMembers) {
  if (!parsedUsers || !Array.isArray(parsedUsers)) return [];
  if (!companyMembers || !Array.isArray(companyMembers)) {
    return parsedUsers.map((user) => ({
      rawName: user.rawName,
      email: user.email,
      rowIndex: user.rowIndex,
      matchType: 'none',
      confidence: 0,
      matchedProfile: null,
      action: 'create_new',
    }));
  }

  // Pre-build lookup maps for performance
  const emailMap = new Map();
  for (const member of companyMembers) {
    if (member.email) {
      emailMap.set(member.email.toLowerCase().trim(), member);
    }
  }

  const nameMap = new Map();
  for (const member of companyMembers) {
    const fullName = normalize(profileFullName(member));
    if (fullName) {
      nameMap.set(fullName, member);
    }
  }

  return parsedUsers.map((user) => {
    const result = {
      rawName: user.rawName,
      email: user.email,
      rowIndex: user.rowIndex,
      matchType: 'none',
      confidence: 0,
      matchedProfile: null,
      action: 'create_new',
    };

    // 1. Exact email match
    if (user.email) {
      const normalizedEmail = user.email.toLowerCase().trim();
      const emailMatch = emailMap.get(normalizedEmail);
      if (emailMatch) {
        result.matchType = 'email_exact';
        result.confidence = 1.0;
        result.matchedProfile = emailMatch;
        result.action = 'match';
        return result;
      }
    }

    // 2. Exact full name match
    if (user.rawName) {
      const normalizedRaw = normalize(user.rawName);

      // Also try the parsed "First Last" form in case rawName is "Last, First"
      const parsed = parseFullName(user.rawName);
      const parsedFullName = normalize(
        [parsed.firstName, parsed.lastName].filter(Boolean).join(' ')
      );

      const exactMatch =
        nameMap.get(normalizedRaw) || nameMap.get(parsedFullName);

      if (exactMatch) {
        result.matchType = 'name_exact';
        result.confidence = 0.95;
        result.matchedProfile = exactMatch;
        result.action = 'match';
        return result;
      }

      // 3. Fuzzy name match
      let bestScore = 0;
      let bestMember = null;

      const searchName = parsedFullName || normalizedRaw;

      for (const member of companyMembers) {
        const memberName = profileFullName(member);
        if (!memberName) continue;

        const score = fuzzyNameScore(searchName, memberName);
        if (score > bestScore) {
          bestScore = score;
          bestMember = member;
        }
      }

      if (bestScore >= 0.6 && bestMember) {
        result.matchType = 'name_fuzzy';
        result.confidence = bestScore;
        result.matchedProfile = bestMember;
        result.action = 'match';
        return result;
      }
    }

    // 4. No match
    return result;
  });
}
